import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Twitter, Linkedin, Facebook, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useBlog, useBlogs } from '../hooks/useData';

const BlogDetail = () => {
  const { slug } = useParams();
  const { blog, loading } = useBlog(slug);
  const { blogs: allBlogs } = useBlogs();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Article not found
            </h1>
            <Button asChild>
              <Link to="/blog">Browse All Articles</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedPosts = allBlogs.filter((b) => b.id !== blog.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>

          {/* Header */}
          <header className="mb-8">
            <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full mb-4">
              {blog.category}
            </span>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              {blog.title}
            </h1>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={blog.authorAvatar}
                  alt={blog.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-medium text-gray-900">{blog.author}</div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {blog.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {blog.readTime}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 mr-2">Share:</span>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Twitter className="w-4 h-4 text-gray-500" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Linkedin className="w-4 h-4 text-gray-500" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Facebook className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-8">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-[400px] object-cover"
            />
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-12">
            <div className="prose prose-lg max-w-none">
              {blog.content ? (
                <div className="text-gray-600 leading-relaxed">
                  {blog.content.split('\n').map((paragraph, idx) => {
                    // Helper to parse bold text
                    const parseBold = (text) => {
                      if (!text.includes('**')) return text;
                      const parts = text.split(/(\*\*[^*]+\*\*)/g);
                      return parts.map((part, i) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={i}>{part.slice(2, -2)}</strong>;
                        }
                        return part;
                      });
                    };

                    // Handle headings
                    if (paragraph.startsWith('## ')) {
                      return (
                        <h2 key={idx} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                          {paragraph.replace('## ', '')}
                        </h2>
                      );
                    }
                    if (paragraph.startsWith('### ')) {
                      return (
                        <h3 key={idx} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                          {paragraph.replace('### ', '')}
                        </h3>
                      );
                    }
                    // Handle list items
                    if (paragraph.startsWith('- ')) {
                      return (
                        <li key={idx} className="text-gray-600 ml-6 list-disc mb-2">
                          {parseBold(paragraph.replace('- ', ''))}
                        </li>
                      );
                    }
                    // Handle numbered list items
                    if (/^\d+\.\s/.test(paragraph)) {
                      return (
                        <li key={idx} className="text-gray-600 ml-6 list-decimal mb-2">
                          {parseBold(paragraph.replace(/^\d+\.\s/, ''))}
                        </li>
                      );
                    }
                    // Regular paragraphs (including those with bold)
                    if (paragraph.trim()) {
                      return (
                        <p key={idx} className="text-gray-600 mb-4">
                          {parseBold(paragraph)}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>
              ) : (
                <>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {blog.excerpt}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    This article provides valuable insights into the topic. Visit our blog for more expert guides and tool comparisons.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
                  >
                    <div className="h-32 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                        {post.title}
                      </h3>
                      <div className="text-xs text-gray-400 mt-2">
                        {post.readTime}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetail;